import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Output,
  EventEmitter,
  Input,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { TooltipService } from '../directives/tooltip.service';

// ── Measurement config ────────────────────────────────────────────────────────
const NUM_RUNS        = 3;
const CLICK_FREQ      = 1000;
const CLICK_DURATION  = 0.04;
const INTER_RUN_DELAY = 1400;
const DETECT_WINDOW   = 2500;
const DETECT_THRESHOLD = 0.02;

// Click configs mirrored from the PWA — indexed by clickLevel (0/1/2)
type OscConfig = { type: OscillatorType; freq: number; gain: number; dur: number };
const CLICK_CONFIGS: OscConfig[][] = [
  // 0 — Normal: single sine
  [{ type: 'sine',     freq: CLICK_FREQ,     gain: 1.0, dur: CLICK_DURATION }],
  // 1 — Boost: sawtooth (more harmonics, cuts through mix)
  [{ type: 'sawtooth', freq: CLICK_FREQ,     gain: 1.0, dur: CLICK_DURATION * 1.5 }],
  // 2 — Max: sine + square + sawtooth layered
  [{ type: 'sine',     freq: CLICK_FREQ,     gain: 1.0, dur: CLICK_DURATION * 2 },
   { type: 'square',   freq: CLICK_FREQ * 2, gain: 0.5, dur: CLICK_DURATION * 2 },
   { type: 'sawtooth', freq: CLICK_FREQ / 2, gain: 0.4, dur: CLICK_DURATION * 2 }],
];

type RunState = 'idle' | 'active' | 'done' | 'error';
type UIPhase  = 'ready' | 'measuring' | 'done' | 'error';

interface RunResult { state: RunState; ms: number | null; }

@Component({
  selector: 'app-sync-test-modal',
  templateUrl: './sync-test-modal.component.html',
  styleUrls:  ['./sync-test-modal.component.scss'],
})
export class SyncTestModalComponent implements OnInit, AfterViewInit, OnDestroy {

  /** Current sync value held in parent recorder — shown for context */
  @Input() currentSync: number = 0;

  /** Emits the ms value to apply — parent calls onSyncChange(value) */
  @Output() syncApplied = new EventEmitter<number>();

  /** Emits when modal should close */
  @Output() closed = new EventEmitter<void>();

  @ViewChild('vizCanvas') vizCanvasRef!: ElementRef<HTMLCanvasElement>;

  // ── UI state ──────────────────────────────────────────────────────────────
  phase: UIPhase  = 'ready';
  isClosing       = false;
  runs: RunResult[] = Array.from({ length: NUM_RUNS }, () => ({ state: 'idle' as RunState, ms: null }));
  measuredOffset: number | null  = null;
  previousOffset: number | null  = null;
  errorMsg: string | null        = null;
  lastApplied: number | null     = null;
  clickLevel: number            = 0;   // 0=Normal 1=Boost 2=Max
  readonly levelLabels          = ['Normal', 'Boost', 'Max'];
  micLabel: string              = '';

  micBoostOn = localStorage.getItem('ssMicBoostOn') === 'true';

  // ── Audio nodes ────────────────────────────────────────────────────────────
  private audioCtx: AudioContext | null                        = null;
  private playbackCtx: AudioContext | null                     = null;
  private micStream: MediaStream | null                        = null;
  private micSource: MediaStreamAudioSourceNode | null         = null;
  private gainNode: GainNode | null                            = null;
  private analyserNode: AnalyserNode | null                    = null;
  private processorNode: AudioWorkletNode | null               = null;
  private pollRaf: number | null                               = null;
  private detectCallback: ((peak: number) => void) | null      = null;
  private vizRaf: number | null                                = null;
  private workletLoaded                                        = false;
  private playbackCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private cdr: ChangeDetectorRef, readonly tooltipSvc: TooltipService) {}

  ngOnInit(): void {
    const stored = parseInt(localStorage.getItem('sync') ?? '0', 10);
    if (!isNaN(stored) && this.currentSync === 0) this.currentSync = stored;

    const last = parseInt(localStorage.getItem('syncMeterLastOffset') ?? '', 10);
    if (!isNaN(last)) {
      this.previousOffset = last; // shown dimly; phase stays 'ready'
    }

    this.resolveMicLabel();
  }

  private resolveMicLabel(): void {
    const deviceId: string | null = (window as any).__ssRecorderAudioDeviceId ?? null;
    navigator.mediaDevices.enumerateDevices().then(devices => {
      const mic = deviceId
        ? devices.find(d => d.kind === 'audioinput' && d.deviceId === deviceId)
        : devices.find(d => d.kind === 'audioinput');
      const label = mic?.label ?? '';
      this.micLabel = label
        .replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)/i, '')
        .replace(/^Default\s*[-–]\s*/i, '')
        .trim() || 'System default mic';
      this.cdr.detectChanges();
    }).catch(() => {});
  }

  ngAfterViewInit(): void {
    this.resizeCanvas();
  }

  ngOnDestroy(): void {
    this.teardownAudio();
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  get resultClass(): string {
    if (this.measuredOffset === null) return '';
    const a = Math.abs(this.measuredOffset);
    return a < 80 ? 'good' : a < 200 ? 'ok' : 'warn';
  }

  get resultDescription(): string {
    if (this.measuredOffset === null) return '';
    const a = Math.abs(this.measuredOffset);
    if (a < 80)  return 'Very low latency — you may not need any adjustment.';
    if (a < 200) return 'Normal range — apply this value to improve sync.';
    return 'High latency — apply this value or check your audio setup.';
  }

  // ── User actions ──────────────────────────────────────────────────────────

  onBackdropClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('sync-modal-backdrop')) this.close();
  }

  close(): void {
    this.teardownAudio();
    this.closed.emit();
  }

  setClickLevel(level: number): void {
    if (this.phase !== 'measuring') this.clickLevel = level;
  }

  async startTest(): Promise<void> {
    this.phase          = 'measuring';
    this.errorMsg       = null;
    this.measuredOffset = null;
    this.runs           = Array.from({ length: NUM_RUNS }, () => ({ state: 'idle' as RunState, ms: null }));
    this.cdr.detectChanges();

    // Suspend the recorder's AudioContext so it doesn't compete with our measurement.
    const recCtx = (window as any).__ssRecorderAudioCtx as AudioContext | null ?? null;
    if (recCtx && recCtx.state === 'running') await recCtx.suspend();

    const ok = await this.setupMic();
    if (!ok) {
      this.phase = 'error';
      this.cdr.detectChanges();
      if (recCtx && recCtx.state === 'suspended') await recCtx.resume();
      return;
    }

    this.startViz();

    try {
      const results: number[] = [];
      for (let i = 0; i < NUM_RUNS; i++) {
        this.runs[i].state = 'active';
        this.cdr.detectChanges();
        const ms = await this.runOnce(i);
        results.push(ms);
        this.runs[i] = { state: 'done', ms };
        this.cdr.detectChanges();
        if (i < NUM_RUNS - 1) await this.sleep(INTER_RUN_DELAY);
      }
      const avg = Math.round(results.reduce((a, b) => a + b, 0) / results.length);
      this.measuredOffset = -avg;
      localStorage.setItem('syncMeterLastOffset', String(this.measuredOffset));
      this.phase = 'done';
    } catch (err: any) {
      this.errorMsg = err?.message ?? 'Could not detect the click — try moving your mic closer to the speaker, enabling Mic Boost, or raising the Click Volume.';
      this.phase    = 'error';
      this.runs     = this.runs.map(r =>
        r.state === 'active' ? { state: 'error' as RunState, ms: null } : r
      );
    } finally {
      this.teardownAudio();
      if (recCtx && recCtx.state === 'suspended') await recCtx.resume();
      this.cdr.detectChanges();
    }
  }

  reset(): void {
    this.phase          = 'ready';
    this.measuredOffset = null;
    this.errorMsg       = null;
    this.lastApplied    = null;
    this.runs           = Array.from({ length: NUM_RUNS }, () => ({ state: 'idle' as RunState, ms: null }));
    this.cdr.detectChanges();
  }

  applyToRecorder(): void {
    if (this.measuredOffset === null) return;
    this.lastApplied = this.measuredOffset;
    this.currentSync = this.measuredOffset;
    this.syncApplied.emit(this.measuredOffset);
    this.isClosing = true;
    this.cdr.detectChanges();
    setTimeout(() => this.close(), 800);
  }

  resetRecorderSync(): void {
    this.lastApplied = 0;
    this.currentSync = 0;
    this.syncApplied.emit(0);
    this.cdr.detectChanges();
  }

  toggleMicBoost(): void {
    this.micBoostOn = !this.micBoostOn;
    localStorage.setItem('ssMicBoostOn', String(this.micBoostOn));
    this.cdr.detectChanges();
  }

  // ── Mic setup ─────────────────────────────────────────────────────────────

  private async setupMic(): Promise<boolean> {
    this.workletLoaded = false;
    try {
      this.audioCtx = new AudioContext({ latencyHint: 'interactive' });
      if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();

      // Always request a fresh stream — never borrow the recorder's boosted dest-track stream.
      // Reusing the recorder's MediaStreamDestination track in a second AudioContext
      // disturbs Chrome's internal track routing and silently reduces the recorder's mic level.
      const selectedDeviceId: string | null = (window as any).__ssRecorderAudioDeviceId ?? null;
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {}),
      };
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints, video: false });

      this.micSource = this.audioCtx.createMediaStreamSource(this.micStream);

      // Optional mic boost — uses the same calibration value as the recorder.
      this.gainNode = this.audioCtx.createGain();
      if (this.micBoostOn) {
        const saved = parseFloat(localStorage.getItem('ssMicBoost') || '0');
        const isFirefox = /Firefox/.test(navigator.userAgent);
        this.gainNode.gain.value = saved > 1 ? saved : (isFirefox ? 20 : 5);
      } else {
        this.gainNode.gain.value = 1;
      }
      this.micSource.connect(this.gainNode);

      this.analyserNode = this.audioCtx.createAnalyser();
      this.analyserNode.fftSize = 256;
      this.gainNode.connect(this.analyserNode);

      // AudioWorklet runs in the audio thread — no main-thread jitter, timing is accurate.
      try {
        await this.audioCtx.audioWorklet.addModule('/assets/build-recorder/peak-detector.js');
        this.processorNode = new AudioWorkletNode(this.audioCtx, 'peak-detector');
        this.processorNode.port.onmessage = ({ data }: MessageEvent) => {
          this.detectCallback?.(data.peak);
        };
        this.gainNode!.connect(this.processorNode);
        this.workletLoaded = true;
      } catch {
        // Fallback: poll the analyserNode via rAF — less precise but broadly compatible.
        this.workletLoaded = false;
        const byteBuf = new Uint8Array(this.analyserNode.fftSize);
        const pollPeak = () => {
          this.analyserNode!.getByteTimeDomainData(byteBuf);
          let peak = 0;
          for (let i = 0; i < byteBuf.length; i++) {
            const dev = Math.abs(byteBuf[i] - 128) / 128;
            if (dev > peak) peak = dev;
          }
          this.detectCallback?.(peak);
          this.pollRaf = requestAnimationFrame(pollPeak);
        };
        this.pollRaf = requestAnimationFrame(pollPeak);
      }

      return true;
    } catch (err: any) {
      const msg = String(err?.message ?? err).toLowerCase();
      this.errorMsg = msg.includes('permission') || msg.includes('denied')
        ? 'Microphone access denied. Please allow mic access and try again.'
        : 'Could not access microphone: ' + err?.message;
      return false;
    }
  }

  private teardownAudio(): void {
    if (this.vizRaf  !== null) { cancelAnimationFrame(this.vizRaf);  this.vizRaf  = null; }
    if (this.pollRaf !== null) { cancelAnimationFrame(this.pollRaf); this.pollRaf = null; }
    this.detectCallback = null;
    if (this.processorNode) {
      try { this.processorNode.port.onmessage = null; } catch {}
      try { this.processorNode.disconnect(); } catch {}
      this.processorNode = null;
    }
    try { this.analyserNode?.disconnect(); } catch {}
    try { this.gainNode?.disconnect();     } catch {}
    try { this.micSource?.disconnect();    } catch {}
    if (this.micStream) {
      this.micStream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    }
    if (this.playbackCloseTimer !== null) { clearTimeout(this.playbackCloseTimer); this.playbackCloseTimer = null; }
    this.audioCtx?.close().catch(() => {});
    this.playbackCtx?.close().catch(() => {});
    this.audioCtx = this.playbackCtx = this.micStream = this.micSource = this.analyserNode = null;
  }

  // ── Single measurement run ────────────────────────────────────────────────

  private runOnce(runIndex: number): Promise<number> {
    return new Promise((resolve, reject) => {
      let detected = false;

      setTimeout(() => {
        // playClick() uses a separate AudioContext so the mic context is untouched.
        // It returns how many ms until the click actually fires (scheduled 50ms ahead).
        const msUntilFire = this.playClick();
        const t0          = performance.now() + msUntilFire;
        const ignoreUntil = t0 + 50; // skip direct bleed in the first 50 ms

        const timeoutId = setTimeout(() => {
          if (!detected) {
            this.detectCallback = null;
            reject(new Error(`Run ${runIndex + 1}: click not heard — raise speaker volume or move mic closer.`));
          }
        }, DETECT_WINDOW + msUntilFire);

        this.detectCallback = (peak: number) => {
          if (detected) return;
          const now = performance.now();
          if (now < ignoreUntil) return;
          if (peak >= DETECT_THRESHOLD) {
            detected = true;
            this.detectCallback = null;
            clearTimeout(timeoutId);
            resolve(Math.round(now - t0));
          }
        };
      }, 400);
    });
  }

  // ── Click synthesis ───────────────────────────────────────────────────────
  // Uses its own AudioContext so click output is completely separate from the
  // mic-capture graph. Returns the number of ms until the click actually fires.

  private playClick(): number {
    const ctx       = new AudioContext();
    const startTime = ctx.currentTime + 0.05;
    const oscs      = CLICK_CONFIGS[this.clickLevel];
    const master    = ctx.createGain();
    master.gain.value = 1.0;
    master.connect(ctx.destination);

    oscs.forEach(({ type, freq, gain, dur }) => {
      const osc  = ctx.createOscillator();
      const gn   = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gn.gain.setValueAtTime(0, startTime);
      gn.gain.linearRampToValueAtTime(gain, startTime + 0.002);
      gn.gain.linearRampToValueAtTime(0,    startTime + dur);
      osc.connect(gn);
      gn.connect(master);
      osc.start(startTime);
      osc.stop(startTime + dur + 0.01);
    });

    const maxDur = Math.max(...oscs.map(o => o.dur));
    if (this.playbackCloseTimer !== null) clearTimeout(this.playbackCloseTimer);
    this.playbackCtx = ctx;
    this.playbackCloseTimer = setTimeout(() => {
      this.playbackCtx?.close().catch(() => {});
      this.playbackCtx = null;
      this.playbackCloseTimer = null;
    }, (maxDur + 0.2) * 1000);
    return (startTime - ctx.currentTime) * 1000;
  }

  // ── Waveform visualisation ────────────────────────────────────────────────

  private startViz(): void {
    const canvas = this.vizCanvasRef?.nativeElement;
    if (!canvas || !this.analyserNode) return;

    const draw = () => {
      if (!this.analyserNode || this.phase !== 'measuring') return;
      this.vizRaf = requestAnimationFrame(draw);

      const w   = canvas.width;
      const h   = canvas.height;
      const ctx = canvas.getContext('2d')!;
      const buf  = new Uint8Array(this.analyserNode.frequencyBinCount);
      this.analyserNode.getByteTimeDomainData(buf);

      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = '#34d399';
      ctx.lineWidth   = 1.5 * devicePixelRatio;
      ctx.beginPath();
      const slice = w / buf.length;
      let x = 0;
      for (let i = 0; i < buf.length; i++) {
        const y = (buf[i] / 128.0) * (h / 2);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        x += slice;
      }
      ctx.stroke();
    };
    draw();
  }

  private resizeCanvas(): void {
    const canvas = this.vizCanvasRef?.nativeElement;
    if (!canvas) return;
    canvas.width  = canvas.offsetWidth  * devicePixelRatio;
    canvas.height = canvas.offsetHeight * devicePixelRatio;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}
